import { Component, EventEmitter, Input, Output } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { MatButtonModule } from '@angular/material/button';
import { DateAdapter, MAT_DATE_LOCALE, provideNativeDateAdapter } from '@angular/material/core';
import { MatDatepickerModule } from '@angular/material/datepicker';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatIconModule } from '@angular/material/icon';
import { MatInputModule } from '@angular/material/input';
import { MatSelectModule } from '@angular/material/select';
import { MatTooltipModule } from '@angular/material/tooltip';
import { NgxCurrencyDirective } from 'ngx-currency';
import { EmptyStateComponent } from '@shared/components/empty-state/empty-state.component';
import { AlertService, AlertType } from '@shared/services/alert.service';
import { CustomDateAdapter } from '@shared/services/custom-date-adapter';
import { MaskService } from '@shared/services/mask.service';
import { FormaPagamento } from '@shared/services/models/financas/forma-pagamento.model';
import {
  CotacaoParcela,
  CotacaoParcelasState,
  dataPorDias,
  gerarParcelasDe,
  inicioDoDia,
  parseParcelasDias,
} from '../card-edit-dialog/card-edit-dialog.types';

/**
 * Editor da condição de pagamento de UMA cotação: expressão ("30 60", "3x", "15 +2x"), tabela de
 * parcelas com dias ↔ data sincronizados e forma de pagamento. Usado pela etapa de Cotação
 * e pelo painel da matriz de comparação.
 *
 * Contrato: muta `state` no lugar (quem o possui é que persiste) e emite `changed` a cada
 * alteração, para o pai marcar sujeira.
 */
@Component({
  selector: 'app-cotacao-parcelas-editor',
  standalone: true,
  imports: [
    FormsModule,
    EmptyStateComponent,
    MatButtonModule,
    MatDatepickerModule,
    MatFormFieldModule,
    MatIconModule,
    MatInputModule,
    MatSelectModule,
    MatTooltipModule,
    NgxCurrencyDirective,
  ],
  providers: [
    provideNativeDateAdapter(),
    { provide: MAT_DATE_LOCALE, useValue: 'pt-BR' },
    { provide: DateAdapter, useClass: CustomDateAdapter },
  ],
  templateUrl: './cotacao-parcelas-editor.component.html',
  styleUrl: './cotacao-parcelas-editor.component.css',
})
export class CotacaoParcelasEditorComponent {
  @Input({ required: true }) state!: CotacaoParcelasState;
  /** Data-base da sessão — mesma instância nas duas telas para dias↔data não divergirem. */
  @Input() dataBase: Date = inicioDoDia(new Date());
  /** Esconde a dica textual sob a expressão (painel lateral tem pouco espaço). */
  @Input() compacto = false;
  /** Cotação arquivada / etapa terminal: as parcelas ficam visíveis, mas sem edição. */
  @Input() somenteLeitura = false;
  /** Formas de pagamento (cadastro de Finanças) do select da parcela — carregadas uma vez por quem usa o editor. */
  @Input() formasPagamento: FormaPagamento[] = [];

  @Output() changed = new EventEmitter<void>();

  diasMask: ReturnType<MaskService['getIntegerMask']>;

  constructor(
    private alertService: AlertService,
    private maskService: MaskService,
  ) {
    this.diasMask = this.maskService.getIntegerMask();
  }

  onDateInput(event: Event): void {
    MaskService.onDateInput(event);
  }

  normalizarExpressao(): void {
    this.state.expr = this.state.expr.replace(/-/g, '');
    this.changed.emit();
  }

  gerar(): void {
    const parcelas = gerarParcelasDe(this.dataBase, this.state.expr);
    if (parcelas.length === 0) {
      this.state.parcelas = [];
      if (this.state.expr.trim()) {
        this.alertService.show('Atenção', 'Informe apenas prazos iguais ou maiores que zero.', AlertType.warning);
      }
      this.changed.emit();
      return;
    }
    this.state.parcelas = parcelas;
    this.changed.emit();
  }

  adicionar(): void {
    const ultima = this.state.parcelas.length ? this.state.parcelas[this.state.parcelas.length - 1].dias + 30 : 30;
    this.state.parcelas = [...this.state.parcelas, { dias: ultima, data: dataPorDias(this.dataBase, ultima), idFormaPagamento: null }];
    this.sincronizarExpressao();
    this.changed.emit();
  }

  remover(indice: number): void {
    this.state.parcelas = this.state.parcelas.filter((_, j) => j !== indice);
    this.sincronizarExpressao();
    this.changed.emit();
  }

  atualizarData(parcela: CotacaoParcela, valor?: number | string): void {
    const sincronizar = () => {
      const dias = Math.max(0, Math.trunc(Number(valor ?? parcela.dias) || 0));
      parcela.dias = dias;
      parcela.data = dataPorDias(this.dataBase, dias);
      this.sincronizarExpressao();
    };

    sincronizar();
    // O ngx-currency atualiza o ngModel após emitir o evento em alguns casos (especialmente ao
    // digitar o sinal negativo). Reaplicamos após o ciclo atual para a trava de zero prevalecer.
    queueMicrotask(sincronizar);
    this.changed.emit();
  }

  atualizarDias(parcela: CotacaoParcela): void {
    if (!(parcela.data instanceof Date) || Number.isNaN(parcela.data.getTime())) return;

    const data = inicioDoDia(parcela.data);
    const dias = Math.max(0, Math.round((data.getTime() - this.dataBase.getTime()) / 86_400_000));
    parcela.dias = dias;
    parcela.data = dataPorDias(this.dataBase, dias);
    this.sincronizarExpressao();
    this.changed.emit();
  }

  /**
   * Mantém a expressão coerente com a tabela — é ela que é gravada em `condicaoPagamento`, então
   * sem isto mudar as parcelas para 30/60 gravava a expressão antiga. Só reescreve quando a
   * expressão deixou de descrever as parcelas, para preservar atalhos como "3x".
   */
  private sincronizarExpressao(): void {
    const dias = this.state.parcelas.map(p => Math.max(0, Math.trunc(Number(p.dias) || 0)));
    const daExpressao = parseParcelasDias(this.state.expr);
    const equivalente = daExpressao.length === dias.length && daExpressao.every((d, i) => d === dias[i]);
    if (!equivalente) this.state.expr = dias.join(' ');
  }
}
